/**
 * reCAPTCHA Token Handler
 * Handles 401 unauthorized errors related to reCAPTCHA tokens
 */

export type AntiBotUnavailableReason = 'NO_REFRESH_FN' | 'REFRESH_FAILED' | 'TOKEN_NULL' | 'SDK_NOT_READY';

export class AntiBotUnavailableError extends Error {
  public readonly reason: AntiBotUnavailableReason;

  constructor(message: string, reason: AntiBotUnavailableReason) {
    super(message);
    this.name = 'AntiBotUnavailableError';
    this.reason = reason;
  }
}

/**
 * Global registry for token refresh function
 * This allows request functions to automatically refresh tokens without needing React context
 */
let globalTokenRefreshFn: GetNewRecaptchaTokenFn | null = null;

/**
 * Set the global token refresh function
 * This should be called from RecaptchaContext when it mounts
 */
export function setGlobalTokenRefreshFn(fn: GetNewRecaptchaTokenFn | null): void {
  globalTokenRefreshFn = fn;
}

/**
 * Get the global token refresh function
 */
export function getGlobalTokenRefreshFn(): GetNewRecaptchaTokenFn | null {
  return globalTokenRefreshFn;
}

/**
 * Check if error is a 401 Unauthorized error
 */
function hasUnauthorizedOrForbiddenStatus(error: any): boolean {
  const directStatus = error?.status;
  const responseStatus = error?.response?.status;
  return directStatus === 401 || directStatus === 403 ||
         responseStatus === 401 || responseStatus === 403;
}

function isUnauthorizedString(error: any): boolean {
  return typeof error === 'string' && error.includes('Unauthorized');
}

function isUnauthorizedError(error: any): boolean {
  return hasUnauthorizedOrForbiddenStatus(error) || isUnauthorizedString(error);
}

/**
 * Check if error message contains reCAPTCHA-related keywords
 */
const RECAPTCHA_KEYWORDS = ['anti-bot', 'recaptcha', 'x-anti-bot'];

function getDirectErrorMessage(error: any): string | null {
  if (error && error.message) return error.message;
  return null;
}

function getBodyErrorMessage(error: any): string | null {
  if (error && error.body && error.body.message) return error.body.message;
  return null;
}

function extractErrorMessage(error: any): string {
  return getDirectErrorMessage(error) ?? getBodyErrorMessage(error) ?? '';
}

function normalizeToLowerCase(value: unknown): string {
  return typeof value === 'string' ? value.toLowerCase() : '';
}

function hasRecaptchaKeywords(error: any): boolean {
  const rawMessage = extractErrorMessage(error);
  const lowerCaseMessage = normalizeToLowerCase(rawMessage);
  return RECAPTCHA_KEYWORDS.some(keyword => lowerCaseMessage.includes(keyword));
}

/**
 * Check if error is a 400 Bad Request with anti-bot keywords (AntiBotTokenMissingError)
 */
function hasAntiBotBadRequestStatus(error: any): boolean {
  const directStatus = error?.status;
  const responseStatus = error?.response?.status;
  return directStatus === 400 || responseStatus === 400;
}

const ANTI_BOT_ERROR_TITLES = ['AntiBotTokenMissingError', 'AntiBotInvalidTokenError'];

function isAntiBotMissingError(error: any): boolean {
  if (!hasAntiBotBadRequestStatus(error)) return false;
  const bodyTitle = error?.body?.title;
  if (typeof bodyTitle === 'string' && ANTI_BOT_ERROR_TITLES.includes(bodyTitle)) return true;
  if (typeof bodyTitle === 'string' && RECAPTCHA_KEYWORDS.some(k => bodyTitle.toLowerCase().includes(k))) return true;
  return hasRecaptchaKeywords(error);
}

/**
 * Check if a 401 error is related to reCAPTCHA token
 * This is determined by checking if the request had an X-Anti-Bot header
 */
export function isRecaptchaTokenError(error: any, hadAntiBotToken: boolean): boolean {
  if (!error) return false;
  if (isAntiBotMissingError(error)) return true;
  if (!isUnauthorizedError(error)) return false;
  if (hasRecaptchaKeywords(error)) return true;
  const directStatus = error?.status;
  const responseStatus = error?.response?.status;
  if (directStatus === 403 || responseStatus === 403) return false;
  return hadAntiBotToken;
}

/**
 * Get a new reCAPTCHA token
 * This function should be called from a component that has access to RecaptchaContext
 */
export type GetNewRecaptchaTokenFn = () => Promise<string | null>;

/**
 * Wraps an API request function with automatic retry on invalid anti-bot token errors.
 * If a 401 error is detected and it's related to an invalid anti-bot token,
 * this function will automatically refresh the token and retry the request once.
 * Uses the global token refresh function if available.
 * 
 * @param requestFn - The API request function to wrap
 * @param requestParams - Parameters to pass to the request function (as an array)
 * @param antiBotToken - The current anti-bot token (if any)
 * @param updateTokenParamFn - Function to update the anti-bot token parameter in requestParams
 * @param getNewTokenFn - Optional function to get a new anti-bot token (uses global if not provided)
 * @returns The result of the request function
 */
const RECOVERY_LEVELS = [
  { name: 'fresh_token', delay: 0 },
  { name: 'widget_reset', delay: 1000 },
  { name: 'sdk_reload', delay: 2000 },
  { name: 'clear_storage_and_reload', delay: 2500 },
] as const;

type RecoveryLevelName = typeof RECOVERY_LEVELS[number]['name'];

let _registeredSiteKey: string | null = null;

/** Register the reCAPTCHA site key for use by recovery utilities. */
export function registerRecaptchaSiteKey(siteKey: string): void {
  _registeredSiteKey = siteKey;
}

/** Remove and re-inject the reCAPTCHA Enterprise script tag. */
async function reloadRecaptchaSdk(siteKey: string): Promise<boolean> {
  if (typeof document === 'undefined') return false;
  const existing = document.getElementById('recaptcha-enterprise-script');
  if (existing) existing.remove();
  delete (window as any).grecaptcha;

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.id = 'recaptcha-enterprise-script';
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
    setTimeout(() => resolve(false), 10000);
  });
}

/** Clear localStorage, sessionStorage, and cookies related to reCAPTCHA. */
function clearRecaptchaStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && (key.includes('recaptcha') || key.includes('grecaptcha') || key.includes('rc::'))) {
        localStorage.removeItem(key);
      }
    }
  } catch { /* storage unavailable */ }
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && (key.includes('recaptcha') || key.includes('grecaptcha') || key.includes('rc::'))) {
        sessionStorage.removeItem(key);
      }
    }
  } catch { /* storage unavailable */ }
  try {
    document.cookie.split(';').forEach(c => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('_GRECAPTCHA') || name.startsWith('rc::')) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });
  } catch { /* cookie API unavailable */ }
}

/** Reset the reCAPTCHA widget reference (if RecaptchaManager exposes a global reset). */
function resetRecaptchaWidget(): void {
  if (typeof window === 'undefined') return;
  const grecaptcha = (window as any).grecaptcha;
  if (grecaptcha?.enterprise?.reset) {
    try { grecaptcha.enterprise.reset(); } catch { /* ignore */ }
  }
}

/** Execute a single recovery level and return a fresh token if successful. */
async function executeRecoveryLevel(
  level: RecoveryLevelName,
  getNewTokenFn: GetNewRecaptchaTokenFn
): Promise<string | null> {
  switch (level) {
    case 'fresh_token':
      return await getNewTokenFn();

    case 'widget_reset':
      resetRecaptchaWidget();
      return await getNewTokenFn();

    case 'sdk_reload': {
      const siteKey = _registeredSiteKey;
      if (!siteKey) return await getNewTokenFn();
      const loaded = await reloadRecaptchaSdk(siteKey);
      if (!loaded) return null;
      await waitForSdkReady(8000);
      return await getNewTokenFn();
    }

    case 'clear_storage_and_reload': {
      clearRecaptchaStorage();
      const siteKey = _registeredSiteKey;
      if (!siteKey) return null;
      const loaded = await reloadRecaptchaSdk(siteKey);
      if (!loaded) return null;
      await waitForSdkReady(8000);
      return await getNewTokenFn();
    }
  }
}

/**
 * Attempt escalating recovery: cycle through recovery levels (fresh token →
 * widget reset → SDK reload → clear storage + reload). Each level is tried
 * once; if it produces a valid token and the request succeeds, we return.
 * Otherwise, escalate to the next level.
 */
async function attemptTokenRefreshAndRetry<T>(
  requestFn: (...args: any[]) => Promise<T>,
  requestParams: any[],
  updateTokenParamFn: (params: any[], newToken: string) => any[],
  getNewTokenFn: GetNewRecaptchaTokenFn,
  originalError: any
): Promise<T> {
  console.warn('[AntiBotTokenRetry] Invalid anti-bot token detected, starting escalating recovery...');

  for (const level of RECOVERY_LEVELS) {
    if (level.delay > 0) {
      await new Promise(resolve => setTimeout(resolve, level.delay));
    }

    let newToken: string | null;
    try {
      newToken = await executeRecoveryLevel(level.name, getNewTokenFn);
    } catch (refreshError) {
      console.warn(`[AntiBotTokenRetry] Recovery level "${level.name}" threw:`, refreshError);
      continue;
    }

    if (!newToken) {
      console.warn(`[AntiBotTokenRetry] Recovery level "${level.name}" returned no token, escalating...`);
      continue;
    }

    const updatedParams = updateTokenParamFn(requestParams, newToken);
    try {
      return await requestFn(...updatedParams);
    } catch (retryError) {
      if (!isRecaptchaTokenError(retryError, true)) {
        throw retryError;
      }
      console.warn(`[AntiBotTokenRetry] Recovery level "${level.name}" request failed, escalating...`);
    }
  }

  console.error('[AntiBotTokenRetry] All recovery levels exhausted, throwing original error');
  throw originalError;
}

function resolveTokenRefreshFn(getNewTokenFn?: GetNewRecaptchaTokenFn): GetNewRecaptchaTokenFn | null {
  return getNewTokenFn || globalTokenRefreshFn;
}

/**
 * Pre-request gate: ensures a valid anti-bot token exists before any API call.
 * If a token is already provided, returns it. Otherwise, attempts to obtain one
 * via the global token refresh function. Throws if no token can be obtained.
 */
/**
 * Wait briefly for globalTokenRefreshFn to be registered.
 * This handles the case where the RecaptchaProvider briefly unmounts
 * during navigation and re-mounts shortly after.
 */
const REFRESH_FN_GRACE_PERIOD_MS = 1500;
const REFRESH_FN_POLL_INTERVAL_MS = 100;
const SDK_READY_TIMEOUT_MS = 15000;
const SDK_READY_POLL_INTERVAL_MS = 200;

/**
 * Block until the reCAPTCHA Enterprise SDK is fully loaded and executable.
 * Prevents API calls from firing with empty/invalid tokens on slow connections.
 */
async function waitForSdkReady(timeoutMs = SDK_READY_TIMEOUT_MS): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const enterprise = (window as any).grecaptcha?.enterprise;
  if (enterprise && typeof enterprise.execute === 'function') return true;

  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await new Promise(resolve => setTimeout(resolve, SDK_READY_POLL_INTERVAL_MS));
    const ent = (window as any).grecaptcha?.enterprise;
    if (ent && typeof ent.execute === 'function') return true;
  }
  return false;
}

async function waitForRefreshFn(): Promise<GetNewRecaptchaTokenFn | null> {
  const startTime = Date.now();

  while (Date.now() - startTime < REFRESH_FN_GRACE_PERIOD_MS) {
    if (globalTokenRefreshFn) return globalTokenRefreshFn;
    await new Promise(resolve => setTimeout(resolve, REFRESH_FN_POLL_INTERVAL_MS));
  }

  return globalTokenRefreshFn;
}

export async function resolveAntiBotToken(
  existingToken?: string | null
): Promise<string> {
  if (existingToken) return existingToken;

  let refreshFn = globalTokenRefreshFn;

  if (!refreshFn) {
    refreshFn = await waitForRefreshFn();
  }

  if (!refreshFn) {
    throw new AntiBotUnavailableError(
      'Anti-bot token is required but unavailable: no token provided and no refresh function registered.',
      'NO_REFRESH_FN'
    );
  }

  let token: string | null;
  try {
    token = await refreshFn();
  } catch (refreshError) {
    throw new AntiBotUnavailableError(
      'Anti-bot token is required but unavailable: token refresh function failed.',
      'REFRESH_FAILED'
    );
  }

  if (!token) {
    throw new AntiBotUnavailableError(
      'Anti-bot token is required but unavailable: refresh function returned no token.',
      'TOKEN_NULL'
    );
  }

  return token;
}

export async function withAntiBotTokenRetry<T>(
  requestFn: (...args: any[]) => Promise<T>,
  requestParams: any[],
  antiBotToken: string | undefined | null,
  updateTokenParamFn: (params: any[], newToken: string) => any[],
  getNewTokenFn?: GetNewRecaptchaTokenFn
): Promise<T> {
  const hadAntiBotToken = !!antiBotToken;
  
  try {
    return await requestFn(...requestParams);
  } catch (error) {
    if (!isRecaptchaTokenError(error, hadAntiBotToken)) throw error;
    
    const tokenRefreshFn = resolveTokenRefreshFn(getNewTokenFn);
    if (!tokenRefreshFn) {
      console.warn('[AntiBotTokenRetry] No token refresh function available, cannot retry');
      throw error;
    }
    
    return attemptTokenRefreshAndRetry(requestFn, requestParams, updateTokenParamFn, tokenRefreshFn, error);
  }
}

